import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

// Component & Asset Imports
import Info from "../../assets/icons/Info.svg"
import Toptext from '../../components/Toptext';
import DynamicHeading from '../../components/ui/CustomHeading';
import { CircleCheck, Quote } from 'lucide-react';
import Timeline from '../../components/Timeline';
import SMEImage1 from "../../assets/images/sme-about-1.png"
import SMEImage2 from "../../assets/images/sme-about-2.png"
import SMEImage3 from "../../assets/images/sme-about3.jpg"
import SubHeading from '../../components/ui/SubHeading';

gsap.registerPlugin(ScrollTrigger);

const AboutPage = () => {
    const mainRef = useRef(null);
    const images = Array.from({ length: 8 }, (_, i) => `https://picsum.photos/300/300?random=${i}`);
    const products = [
        { id: 1, name: "HYPER-INFLATION", description: "In today's hyperinflationary challenges, we pioneer FinTech solutions for economic uncertainties and financial inclusion. Through digital mining, DeFi ecosystems, and algorithmic trading, we offer alternative asset generation, secure digital transactions, and algo-trading solutions, empowering individuals and businesses to thrive in volatile financial landscapes.", image: SMEImage1 },
        { id: 2, name: "PRACTICAL APPLIED A.I", description: "We utilize AI vision technologies to tackle global challenges like road damage detection, unmarked road navigation, and advancing autonomous transport. From enhancing safety with surveillance systems to optimizing healthcare diagnostics and energy usage, our AI vision solutions meet diverse societal needs. Trusted by clients like the RAF and NHS in the UK, our systems deliver innovative solutions.", image: SMEImage2 },
        { id: 3, name: "ROBOTICS AT SCALE", description: "We're scaling up robotics in India to tackle labor shortages and boost delivery and transportation efficiency. By implementing robotics in manufacturing, logistics, and last-mile delivery, we streamline operations and boost productivity, fostering economic growth.", image: SMEImage3 },
    ];

    // Scroll to top on page load
    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = 'About & Portfolio | JBS Capacitors';
    }, []);

    // GSAP Animations
    useGSAP(() => {
        // Hero section intro timeline
        const tl = gsap.timeline({ defaults: { ease: 'power3.out', duration: 0.8 } });
        tl.from('.anim-toptext', { y: -30, opacity: 0 })
          .from('.anim-heading span', { y: 40, opacity: 0, stagger: 0.1 }, "-=0.5")
          .from('.anim-subheading', { y: 20, opacity: 0 }, "-=0.6")
          .fromTo('.anim-line', { scaleX: 0 }, { scaleX: 1, transformOrigin: 'center center', duration: 1 }, "-=0.5")
          .from('.anim-line-icon', { scale: 0, opacity: 0, ease: 'back.out(1.7)' }, "-=0.5");

        // Scroll-triggered animations for sections
        const animateOnScroll = (selector, props = { y: 50, opacity: 0 }) => {
            gsap.from(selector, {
                ...props,
                stagger: 0.2,
                duration: 1,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: selector,
                    start: 'top 85%',
                    toggleActions: 'play none none none',
                }
            });
        };

        animateOnScroll('.anim-timeline');
        animateOnScroll('.anim-quote-block', { scale: 0.9, opacity: 0 });
        animateOnScroll('.anim-pillars-heading');
        animateOnScroll('.anim-sme-heading');
        
        // Staggered animation for SME cards
        document.querySelectorAll('.anim-sme-card').forEach((card, index) => {
            gsap.from(card, {
                x: index % 2 === 0 ? -100 : 100, // Animate from left or right
                opacity: 0,
                duration: 1,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: card,
                    start: 'top 85%',
                    toggleActions: 'play none none none',
                }
            });
        });

    }, { scope: mainRef });

    // Framer Motion Variants for Image Grid
    const gridContainerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.08 }
        }
    };

    const gridItemVariants = {
        hidden: { opacity: 0, scale: 0.8, y: 50 },
        visible: { opacity: 1, scale: 1, y: 0, transition: { type: 'spring', stiffness: 100 } }
    };

    return (
        <div ref={mainRef} className='mt-32 flex flex-col items-center justify-center overflow-x-hidden'>
            {/* Top Heading */}
            <div className="anim-toptext">
                <Toptext icon={Info}>About Our Legacy</Toptext>
            </div>

            {/* Main Section */}
            <div className='mt-12 text-center'>
                <div className="flex flex-col mx-auto max-w-[90%] sm:max-w-[85%] md:max-w-[75%] lg:max-w-[70%] xl:max-w-[60%]">
                    <div className="anim-heading">
                        <DynamicHeading parts={[
                            { text: 'JBS Capacitors', highlight: true }, { text: '— Future-Ready' }, { lineBreak: true },
                            { text: 'Technologies for a Better' }, { text: 'World', highlight: true }, { text: 'Today' },
                        ]} />
                    </div>
                    <div className="anim-subheading">
                        <SubHeading>Crafting the Future with a Legacy of Precision</SubHeading>
                    </div>
                    <div className="relative w-[50%] mx-auto mt-12">
                        <div className="anim-line h-[4px] rounded-full bg-jbsBlue w-full" />
                        <CircleCheck className="anim-line-icon w-10 h-10 bg-white p-1 rounded-full text-jbsBlue absolute -top-4 left-1/2 -translate-x-1/2" />
                    </div>
                </div>

                <div className="anim-timeline">
                    <Timeline />
                </div>

                <div className='anim-quote-block mt-12 max-w-[90%] mx-auto bg-jbsBlue p-6 md:p-12 rounded-[38px] text-white font-bold italic'>
                    <div className="flex flex-col md:flex-row items-start gap-6">
                        <div className="w-full md:w-1/5 flex justify-center"><Quote className='w-16 md:w-24 h-auto text-white' /></div>
                        <div className="w-full md:w-4/5">
                            <h1 className='text-base md:text-[24px] text-left leading-relaxed'>
                                <span className="text-[28px] md:text-[32px] leading-none align-top">"</span>
                                At JBS, we believe in constant evolution. Our strategic diversification and iterative restructuring ensure we remain at the forefront of the ever-changing global market. While our roots lie in electronics manufacturing, we now actively shape the future through innovation in AI, robotics, and fintech."
                                <br /> — Dr. Gayatri Mohapatra, Managing Director
                            </h1>
                        </div>
                    </div>
                </div>

                <div className='mt-16 anim-pillars-heading'>
                    <DynamicHeading parts={[
                        { text: 'Innovation' }, { text: "Pillars", highlight: true }, { text: ': Solving Real -' },
                        { lineBreak: true }, { text: 'World' }, { text: 'Challenges', highlight: true },
                    ]} />
                </div>

                <motion.section
                    className="py-12 px-4 max-w-7xl mx-auto md:px-12"
                    variants={gridContainerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                >
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-4">
                        {images.map((src, index) => (
                            <motion.div
                                key={index}
                                variants={gridItemVariants}
                                whileHover={{ scale: 1.05, zIndex: 10 }}
                                className="overflow-hidden rounded-[38px] shadow-lg"
                            >
                                <img src={src} alt={`Placeholder ${index + 1}`} className="w-full h-full object-cover" />
                            </motion.div>
                        ))}
                    </div>
                </motion.section>

                <div className='mt-16 anim-sme-heading'>
                    <DynamicHeading parts={[
                        { text: 'SME' }, { text: "Innovation", highlight: true }, { text: ' : Solving Key' },
                        { lineBreak: true }, { text: 'Challenges', highlight: true },
                    ]} />
                </div>

                <section className="py-12 max-w-5xl mx-auto px-4 md:px-12">
                    <div className="space-y-16">
                        {products.map((product) => (
                            <motion.div
                                key={product.id}
                                className="anim-sme-card flex flex-col md:flex-row gap-8 items-center bg-white shadow-xl rounded-[38px] overflow-hidden p-6"
                                whileHover={{ y: -8, boxShadow: "0px 20px 30px rgba(0, 0, 0, 0.1)" }}
                                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                            >
                                <div className="md:w-1/3 w-full">
                                    <div className="w-full h-64 overflow-hidden rounded-3xl">
                                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                                    </div>
                                </div>
                                <div className="flex flex-col justify-between md:w-2/3 w-full text-left">
                                    <div>
                                        <h3 className="text-3xl font-bold mb-4 text-jbsBlue">{product.name}</h3>
                                        <p className="text-gray-600 mb-4">{product.description}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    )
}

export default AboutPage;